<template>
    <div class="white-container pt-6">
        <h4 class="mb-4"><i class="far fa-clipboard-check fa-fw mr-2 has-text-primary"></i>{{ heading }}</h4>

        <router-link :to="{name: 'TripPreferences'}" v-if="trip.questions_enabled">
            <div class="task-menu-item">
                <div class="columns is-gapless is-mobile">
                    <div class="column is-narrow pb-0">
                        <i class="fa-fw mr-2" :class="preferencesDone ? 'fas fa-check-circle has-text-primary' : 'far fa-tasks has-text-primary'"></i>
                    </div>
                    <div class="column pb-0">
                        <strong class="has-text-black" :class="{'is-done': preferencesDone}">Answer your Trip Preferences</strong>
                        <div class="is-size-7">
                            {{ preferencesDone ? 'All questions answered. Thank you!' : 'Answer important questions about your trip.' }}
                        </div>
                    </div>
                </div>
            </div>
        </router-link>

        <router-link :to="{name: 'Profile'}">
            <div class="task-menu-item">
                <div class="columns is-gapless is-mobile">
                    <div class="column is-narrow pb-0">
                        <i class="fa-fw mr-2" :class="profileDone ? 'fas fa-check-circle has-text-primary' : 'far fa-address-card has-text-primary'"></i>
                    </div>
                    <div class="column pb-0">
                        <strong class="has-text-black" :class="{'is-done': profileDone}">Create your Personal Profile</strong>
                        <div class="is-size-7">
                            {{ profileDone ? 'Your profile and photo are set.' : 'Add a photo and let fellow travelers know who you are.' }}
                        </div>
                    </div>
                </div>
            </div>
        </router-link>

        <router-link :to="{name: 'Documents'}">
            <div class="task-menu-item">
                <div class="columns is-gapless is-mobile">
                    <div class="column is-narrow pb-0">
                        <i class="fal fa-fw fa-passport mr-2 has-text-primary"></i>
                    </div>
                    <div class="column pb-0">
                        <strong class="has-text-black">Documents & IDs</strong>
                        <div class="is-size-7">
                            Passport, TSA PreCheck, frequent flyer numbers.
                        </div>
                    </div>
                </div>
            </div>
        </router-link>

        <router-link :to="{name: 'Billing'}" v-if="hasPaymentPlan">
            <div class="task-menu-item">
                <div class="columns is-gapless is-mobile">
                    <div class="column is-narrow pb-0">
                        <i class="fal fa-fw fa-credit-card-front mr-2 has-text-primary"></i>
                    </div>
                    <div class="column pb-0">
                        <strong class="has-text-black">Trip Cost & Payments</strong>
                        <div class="is-size-7">
                            Review your payment plan and schedule.
                        </div>
                    </div>
                </div>
            </div>
        </router-link>

        <router-link :to="{name: 'Payment'}" v-if="hasPaymentPlan">
            <div class="task-menu-item">
                <div class="columns is-gapless is-mobile">
                    <div class="column is-narrow pb-0">
                        <i class="fal fa-fw fa-credit-card mr-2 has-text-primary"></i>
                    </div>
                    <div class="column pb-0">
                        <strong class="has-text-black">Payment Method</strong>
                        <div class="is-size-7">
                            Keep your card on file up to date.
                        </div>
                    </div>
                </div>
            </div>
        </router-link>

        <router-link :to="{name: 'Itinerary'}">
            <div class="task-menu-item">
                <div class="columns is-gapless is-mobile">
                    <div class="column is-narrow pb-0">
                        <i class="fal fa-fw fa-tennis-ball mr-2 has-text-primary"></i>
                    </div>
                    <div class="column pb-0">
                        <strong class="has-text-black">Explore your Trip Itinerary</strong>
                        <div class="is-size-7">
                            Check out the exciting events planned.
                        </div>
                    </div>
                </div>
            </div>
        </router-link>

        <router-link :to="{name: 'Flights'}">
            <div class="task-menu-item">
                <div class="columns is-gapless is-mobile">
                    <div class="column is-narrow pb-0">
                        <i class="fal fa-fw fa-plane mr-2 has-text-primary"></i>
                    </div>
                    <div class="column pb-0">
                        <strong class="has-text-black">Your Flights</strong>
                        <div class="is-size-7">
                            Review or add your flight details.
                        </div>
                    </div>
                </div>
            </div>
        </router-link>

        <router-link :to="{name: 'Travel'}">
            <div class="task-menu-item">
                <div class="columns is-gapless is-mobile">
                    <div class="column is-narrow pb-0">
                        <i class="fal fa-fw fa-suitcase-rolling mr-2 has-text-primary"></i>
                    </div>
                    <div class="column pb-0">
                        <strong class="has-text-black">Requirements, Packing & FAQs</strong>
                        <div class="is-size-7">
                            Review travel requirements and other info.
                        </div>
                    </div>
                </div>
            </div>
        </router-link>
    </div>
</template>

<script>
    // Only two items carry real completion state — trip preferences
    // (registrations.questions_complete) and the profile/photo. Everything
    // else is a plain link; we don't have trustworthy "done" signals for
    // those and requirements vary per trip.
    const PROFILE_FIELDS = [
        'travel_companion', 'favorite_trip', 'favorite_player', 'favorite_sport',
        'tennis_club', 'tennis_pro', 'facebook', 'instagram', 'twitter', 'whatsapp'
    ];

    export default {
        name: 'TodayChecklist',
        props: {
            trip: {type: Object, required: true},
            registration: {type: Object, required: true},
            user: {type: Object, required: true},
            hasPaymentPlan: {type: Boolean, default: false},
            heading: {type: String, default: 'Pre-Travel Tasks'}
        },
        computed: {
            preferencesDone: function() {
                return this.registration.questions_complete === 1;
            },
            profileDone: function() {
                if (!this.user.photo_file) return false;
                return PROFILE_FIELDS.some(f => !!this.user[f]);
            }
        }
    };
</script>

<style lang="scss" scoped>
    .task-menu-item {
        padding: 10px;
        cursor: pointer;
        margin: 0 0 5px 25px;

        &:hover {
            border-radius: 5px;
            background-color: #f4f4f4;
        }

        .is-done {
            color: #7a7a7a !important;
        }
    }
</style>
